import { getWorksheetsFromFile } from "@features/upload/helpers"
import WorksheetPreview from "@features/upload/WorksheetPreview"
import { CircularProgress, Grid, Tab, Tabs, Typography } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"

type FileContentPreviewTableProps = {
  file: File
  maxRows?: number
}

export type PreviewRow = Record<string, string>

export type PreviewTable = {
  rows: PreviewRow[]
}

export type PreviewWorksheet = {
  name: string
  tables: PreviewTable[]
}

const FileContentPreviewTable = ({ file, maxRows }: FileContentPreviewTableProps) => {
  const [worksheets, setWorksheets] = useState<PreviewWorksheet[]>([])
  const [currentWorksheet, setCurrentWorksheet] = useState<PreviewWorksheet | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const updateRows = async () => {
      const parsedWorksheets = await getWorksheetsFromFile(file, maxRows)

      setWorksheets(parsedWorksheets)
      setCurrentWorksheet(parsedWorksheets[0] || null)
    }

    setIsLoading(true)
    updateRows()
      .catch(() =>
        enqueueSnackbar("Failed to parse file content for preview", { variant: "error" }),
      )
      .finally(() => setIsLoading(false))
  }, [file, maxRows])

  if (isLoading)
    return (
      <Grid
        container
        item
        direction="column"
        gap={2}
        sx={{
          mt: "15%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid item>
          <CircularProgress disableShrink />
        </Grid>
        <Grid item>
          <Typography>Loading data...</Typography>
        </Grid>
      </Grid>
    )

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    const selectedWorksheet = worksheets.find((ws) => ws.name === newValue)
    setCurrentWorksheet(selectedWorksheet || null)
  }

  return (
    <>
      {maxRows && (
        <Typography>
          Preview has been limited to <strong>{maxRows} rows</strong>
        </Typography>
      )}

      <Tabs value={currentWorksheet?.name} onChange={handleTabChange} variant="scrollable">
        {worksheets.map((worksheet) => (
          <Tab key={worksheet.name} label={worksheet.name} value={worksheet.name} />
        ))}
      </Tabs>

      {currentWorksheet && <WorksheetPreview worksheet={currentWorksheet} />}
    </>
  )
}

export default FileContentPreviewTable
